import { Crisp } from "crisp-sdk-web";

type CrispSessionData = {
  monday_user_id: number | null | undefined;
  monday_account_id: number | null | undefined;
};

class CrispService {
  private static instance: CrispService | null = null;
  private isConfigured = false;
  private static readonly VISIBILITY_STYLE_ID = "crisp-route-visibility-style";
  private static readonly DIM_STYLE_ID = "crisp-minimized-dim-style";
  private static readonly MINIMIZED_CLASS = "crisp-minimized";

  private constructor() {}

  static getInstance(): CrispService {
    if (!CrispService.instance) {
      CrispService.instance = new CrispService();
    }

    return CrispService.instance;
  }

  configure(websiteId: string): void {
    if (this.isConfigured || !websiteId) {
      return;
    }

    Crisp.configure(websiteId);
    this.isConfigured = true;
  }

  setTokenId(token: string): void {
    Crisp.setTokenId(token);
  }

  setSessionData(data: CrispSessionData): void {
    Crisp.session.setData(data);
  }

  private setDomVisibility(isHidden: boolean): void {
    if (typeof document === "undefined") {
      return;
    }

    const existingStyleTag = document.getElementById(CrispService.VISIBILITY_STYLE_ID);

    if (!isHidden) {
      existingStyleTag?.remove();
      return;
    }

    if (existingStyleTag) {
      return;
    }

    const styleTag = document.createElement("style");
    styleTag.id = CrispService.VISIBILITY_STYLE_ID;
    styleTag.textContent = `
      #crisp-chatbox,
      .crisp-client {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    `;

    document.head.appendChild(styleTag);
  }

  // Dim the Crisp launcher while the chat is minimized so it's less obtrusive
  // (e.g. floating over the item-view composer). Restores full opacity on hover
  // and whenever the chat is open. Driven by the SDK's open/close events and a
  // class on <html> — never dims the open chat window, and avoids depending on
  // Crisp's hashed internal class names. Idempotent: the one-time listener wiring
  // is gated on the injected <style> tag's presence.
  enableMinimizedDim(opacity = 0.5): void {
    if (typeof document === "undefined") {
      return;
    }

    // Idempotent: everything below is one-time setup, gated on the injected
    // <style> tag. Re-calls (e.g. on route change) are no-ops; the registered
    // listeners keep the minimized state current.
    if (document.getElementById(CrispService.DIM_STYLE_ID)) {
      return;
    }

    const setMinimized = (minimized: boolean): void => {
      document.documentElement.classList.toggle(CrispService.MINIMIZED_CLASS, minimized);
    };

    const styleTag = document.createElement("style");
    styleTag.id = CrispService.DIM_STYLE_ID;
    styleTag.textContent = `
      html.${CrispService.MINIMIZED_CLASS} #crisp-chatbox {
        opacity: ${opacity};
        transition: opacity 0.2s ease;
      }
      html.${CrispService.MINIMIZED_CLASS} #crisp-chatbox:hover {
        opacity: 1;
      }
    `;
    document.head.appendChild(styleTag);

    Crisp.chat.onChatOpened(() => setMinimized(false));
    Crisp.chat.onChatClosed(() => setMinimized(true));

    // Assume minimized on load (the chat starts closed). We intentionally do NOT
    // call Crisp.chat.isChatOpened() here: it proxies to window.$crisp.is(),
    // which isn't a function until the Crisp runtime finishes loading and would
    // throw during initial render. The listeners above correct the state the
    // moment the chat opens or closes.
    setMinimized(true);
  }

  showChat(): void {
    this.setDomVisibility(false);
    Crisp.chat.show();
  }

  hideChat(): void {
    this.setDomVisibility(true);
    Crisp.chat.close();
    Crisp.chat.hide();
  }
}

export const crispService = CrispService.getInstance();
